import { collection, onSnapshot, query, where, type QuerySnapshot } from "firebase/firestore";
import { ensureAttendeeCountBackfill } from "@/features/guests/sync-attendee-count";
import { db } from "@/lib/firebase";
import { createSharedSubscription } from "@/lib/firestore/createSharedSubscription";
import { isImpersonationActive } from "@/stores/auth-store";
import { reportError } from "@/lib/report-error";
import type { Wedding } from "@amazing-wedding/shared-types";

function firstWedding(snap: QuerySnapshot): Wedding | null {
  const first = snap.docs[0];
  return first ? { id: first.id, ...(first.data() as Omit<Wedding, "id">) } : null;
}

export const weddingSubscription = createSharedSubscription<Wedding | null>({
  getDefault: () => null,
  attach: (uid, setSnapshot) => {
    let owned: Wedding | null = null;
    let partnered: Wedding | null = null;
    let ownedReady = false;
    let partneredReady = false;

    const publish = () => {
      if (!ownedReady || !partneredReady) {
        setSnapshot({ data: null, loading: true });
        return;
      }
      const wedding = owned ?? partnered;
      setSnapshot({ data: wedding, loading: false });
      // Skip the reconciliation write while an admin is impersonating — they
      // can read this wedding but not write it (firestore.rules).
      if (wedding && typeof wedding.attendeeCount !== "number" && !isImpersonationActive()) {
        void ensureAttendeeCountBackfill(wedding.id).catch((err) =>
          reportError("attendee-backfill", err),
        );
      }
    };

    const unsubOwned = onSnapshot(
      query(collection(db, "weddings"), where("ownerId", "==", uid)),
      (snap) => {
        owned = firstWedding(snap);
        ownedReady = true;
        publish();
      },
      () => {
        ownedReady = true;
        publish();
      },
    );

    const unsubPartnered = onSnapshot(
      query(collection(db, "weddings"), where("partnerId", "==", uid)),
      (snap) => {
        partnered = firstWedding(snap);
        partneredReady = true;
        publish();
      },
      () => {
        partneredReady = true;
        publish();
      },
    );

    return () => {
      unsubOwned();
      unsubPartnered();
    };
  },
});
